import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileCheck2, Loader2 } from "lucide-react";
import { FadeUp } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChecklistCTA() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const download = () => {
    const a = document.createElement("a");
    a.href = "/assets/moving-checklist.pdf";
    a.download = "NextMove-Moving-Checklist.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/checklist-lead`, { email, website });
      setDone(true);
      download();
      toast.success("Checklist downloaded. We'll only email you if there's something genuinely useful.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="checklist-section" className="mx-auto max-w-7xl px-4 sm:px-8 pb-20 sm:pb-28">
      <FadeUp className="relative overflow-hidden rounded-3xl border border-amber-500/30 bg-navy grain px-8 py-14 sm:px-14">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">Free download</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">
              The Ultimate Moving Checklist
            </h2>
            <p className="mt-4 max-w-md text-sm sm:text-base leading-relaxed text-slate-300">
              Eight weeks of move prep condensed into one printable page-by-page plan, from booking your
              removalist to the final box. Built from thousands of real Australian moves.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {["Week-by-week countdown plan", "The change-of-address list everyone forgets", "Move-day survival kit checklist"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <FileCheck2 className="h-4 w-4 text-amber-500" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-2xl">
            {done ? (
              <div data-testid="checklist-success" className="text-center py-6 space-y-4">
                <FileCheck2 className="mx-auto h-12 w-12 text-emerald-500" />
                <p className="font-display text-xl font-black text-navy">Your checklist is downloading.</p>
                <p className="text-sm text-slate-500">Didn't start?</p>
                <button onClick={download} data-testid="checklist-redownload-btn"
                  className="cta-gleam inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-amber-400">
                  <Download className="h-4 w-4" /> Download again
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="checklist-form" className="space-y-4">
                <p className="font-display text-lg font-bold text-navy">Where should we send it?</p>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="checklist-email-input" placeholder="you@example.com.au"
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-amber-500" />
                <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} />
                <button type="submit" disabled={loading} data-testid="checklist-download-btn"
                  className="cta-gleam flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 text-sm font-bold text-navy disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {loading ? "Preparing…" : "Get the free checklist"}
                </button>
                <p className="text-center text-[11px] text-slate-400">Instant download. No spam, ever.</p>
              </form>
            )}
          </div>
        </div>
      </FadeUp>
    </section>
  );
}
